import React, { useState, useEffect } from 'react'
import NumberFormat from 'react-number-format'
import adminService from '../../services/adminService'
import Skeleton from 'react-loading-skeleton'
import { toast } from 'react-toastify'

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null)
  const [usageData, setUsageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('30') // days

  useEffect(() => {
    loadAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      const days = parseInt(timeRange, 10) || 30
      const [dashboardStats, usageAnalytics] = await Promise.all([
        adminService.getDashboardStats({ days }),
        adminService.getUsageAnalytics({ days })
      ])

      setAnalytics(dashboardStats)
      setUsageData(usageAnalytics)
    } catch (error) {
      console.error('Error loading analytics:', error)
      toast.error('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Deep insights into your application usage</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm border">
              <Skeleton height={20} className="mb-2" />
              <Skeleton height={32} className="mb-1" />
              <Skeleton height={16} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const MetricCard = ({ title, value, change, icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg p-6 shadow-sm border">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <div className={`w-6 h-6 text-${color}-600`}>
            {icon}
          </div>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <p className={`text-sm ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? '↗' : '↘'} {Math.abs(change)}%
            </p>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Deep insights into your application usage</p>
        </div>
        
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Revenue"
          value={
            <NumberFormat
              value={analytics?.totalRevenue || 0}
              displayType="text"
              thousandSeparator={true}
              prefix="₹"
              renderText={(value) => value}
            />
          }
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
          color="green"
        />

        <MetricCard
          title="Active Users"
          value={usageData?.activeUsers || 0}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" />
            </svg>
          }
          color="blue"
        />

        <MetricCard
          title="Total Actions"
          value={usageData?.totalActions?.toLocaleString() || '0'}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
          color="purple"
        />

        <MetricCard
          title="Conversion Rate"
          value={
            analytics?.totalUsers > 0 
              ? `${((analytics.paidUsers / analytics.totalUsers) * 100).toFixed(1)}%`
              : '0%'
          }
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          color="orange"
        />
      </div>

      {/* Charts and Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* User Growth */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Users</span>
              <span className="font-semibold">{analytics?.totalUsers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Free Users</span>
              <span className="font-semibold text-gray-600">{analytics?.freeUsers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Paid Users</span>
              <span className="font-semibold text-green-600">{analytics?.paidUsers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">New Users (30d)</span>
              <span className="font-semibold text-blue-600">{analytics?.newUsersLast30Days || 0}</span>
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Revenue</span>
              <span className="font-semibold">
                <NumberFormat
                  value={analytics?.totalRevenue || 0}
                  displayType="text"
                  thousandSeparator={true}
                  prefix="₹"
                  renderText={(value) => value}
                />
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Monthly Revenue</span>
              <span className="font-semibold text-green-600">
                <NumberFormat
                  value={analytics?.monthlyRevenue || 0}
                  displayType="text"
                  thousandSeparator={true}
                  prefix="₹"
                  renderText={(value) => value}
                />
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">ARPU</span>
              <span className="font-semibold">
                ₹{analytics?.paidUsers > 0 
                  ? Math.round(analytics.totalRevenue / analytics.paidUsers)
                  : 0
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top User Actions</h3>
          <div className="space-y-3">
            {usageData && Object.entries(usageData.actionCounts)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 8)
              .map(([action, count]) => (
                <div key={action} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">
                    {action.replace(/_/g, ' ')}
                  </span>
                  <span className="font-medium">{count.toLocaleString()}</span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Resource Usage */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage</h3>
          <div className="space-y-3">
            {usageData && Object.entries(usageData.resourceCounts).map(([resource, data]) => (
              <div key={resource}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600 capitalize">{resource}</span>
                  <span className="text-sm font-medium">{data.used} / {data.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${Math.min((data.used / data.total) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAnalytics